import * as React from 'react'

import { cn } from 'src/lib/utils'

import type { SpaceProps as AntdSpaceProps } from './antd-props'

/* -------------------------------------------------------------------------
 * The SHADCN half of the `src/antd/Space` seam. `./Space.tsx` is the
 * dispatcher that chooses between this and antd's own Space; nothing else
 * imports this file, and nothing reaches it until someone flips the design
 * toggle (`src/design-mode`).
 *
 * Ported from the `update-design` reference branch's `src/antd/Space.tsx`
 * (its Phase 2 slice 11 part C). Divergences from the reference are marked
 * `[development]` below; everything else is the reference verbatim.
 *
 * The EXTERNAL API is antd's own `SpaceProps`, so the 601 files that already
 * import `Space` from `src/antd` switch with no edit at all.
 *
 * Mirrors antd 5.15.4 `es/space/index.js`: preset sizes small/middle/large =
 * 8/16/24px, `size` may be a `[horizontal, vertical]` pair, `direction`
 * defaults to horizontal, and `align` defaults to `center` ONLY when the
 * direction is horizontal and no align was given (index.js:63) — a vertical
 * Space with no align stays stretched, which is what makes the 821 vertical
 * Spaces in this repo full-width.
 *
 * `.ant-space` and `.ant-space-item` are re-emitted, and the ITEM name is
 * required rather than decorative: live surfaces on THIS branch select it —
 * `settings/v2/.../RoleForm` (`.ant-space-item:first-child`),
 * `forms/ticket-chat/FromToInput` (`& .ant-space-item`) and
 * `route-studio/perturbations/WhatIfDropdown`, which positions a dropdown
 * against its trigger's `parentElement` and therefore depends on each child
 * actually being wrapped in its own element. A seam that rendered children
 * bare would break that last one silently.
 *
 * [development] Re-emitting the antd names is safe HERE — unlike `.ant-btn`,
 * which `./Button.shadcn.tsx` must not touch — because antd's Space rules and
 * this seam's utilities agree on every property they both set:
 * `.ant-space{display:inline-flex}`, `-vertical{flex-direction:column}` and
 * `-align-*{align-items:…}` are exactly what `inline-flex` / `flex-col` /
 * `items-*` emit here, and the gaps are inline styles, which outrank any
 * stylesheet. So it does not matter whether antd's (unlayered, and therefore
 * cascade-winning) Space stylesheet happens to be injected while the toggle
 * is on. `.ant-btn` had no such agreement — it carries height, border and
 * background this seam replaces.
 *
 * Nullish children are dropped rather than rendered as empty items, so a
 * `{cond && <X/>}` child does not leave a gap behind — antd does the same.
 * ------------------------------------------------------------------------- */

type SpaceSize = 'small' | 'middle' | 'large' | number

/**
 * antd's preset gaps. These are a COMPAT contract, not a design choice: 1,236
 * call sites pass `size` and 241 of them name a preset, so changing a number
 * here moves layout on those screens. They coincide exactly with the Tailwind
 * spacing scale this branch ships (`--spacing` = 0.25rem → 8/16/24px are
 * `gap-2` / `gap-4` / `gap-6`); they are applied as pixel gaps rather than as
 * those utilities because `size` is far more often a raw number (995 sites,
 * values 0…32) than a preset, and one code path for both is what keeps the
 * pair form (`size={[4, 8]}`) honest.
 */
const PRESET_SIZE: Record<'small' | 'middle' | 'large', number> = {
  small: 8,
  middle: 16,
  large: 24,
}

// [development] antd's own prop type, as `./Button.shadcn.tsx` does: 601 files
// already type against it, and substituting a looser shape widens callback
// parameters to `any` at call sites and breaks `noImplicitAny` in files this
// change never touched.
export type SpaceProps = AntdSpaceProps

const ALIGN_CLASS: Record<string, string> = {
  start: 'items-start',
  end: 'items-end',
  center: 'items-center',
  baseline: 'items-baseline',
}

const toPx = (size: SpaceSize): number =>
  typeof size === 'number' ? size : PRESET_SIZE[size] ?? 0

/**
 * The children that become items, in antd's own terms.
 *
 * [development] `React.Children.toArray` alone — which is what the reference
 * branch uses — is NOT what antd does: antd calls rc-util's `toArray`, which
 * also flattens FRAGMENTS. `<Space>{cond && <><A /><B /></>}</Space>` is two
 * items to antd and would be one to a plain `React.Children.toArray`, i.e. one
 * gap silently missing. rc-util is antd's dependency and goes away with it, so
 * the recursion is written out here rather than imported.
 */
const toItems = (children: React.ReactNode): React.ReactNode[] =>
  React.Children.toArray(children).flatMap((child) =>
    React.isValidElement(child) && child.type === React.Fragment
      ? toItems((child.props as { children?: React.ReactNode }).children)
      : [child]
  )

const SpaceBase = React.forwardRef<HTMLDivElement, SpaceProps>((props, ref) => {
  const {
    size = 'small',
    direction = 'horizontal',
    align,
    wrap = false,
    split,
    className,
    style,
    children,
    // antd-only props that must never reach the DOM
    prefixCls: _prefixCls,
    rootClassName: _rootClassName,
    classNames: _classNames,
    styles: _styles,
    ...rest
  } = props

  const [horizontalSize, verticalSize] = React.useMemo(() => {
    const pair = (Array.isArray(size) ? size : [size, size]) as [
      SpaceSize,
      SpaceSize
    ]
    return [toPx(pair[0]), toPx(pair[1])]
  }, [size])

  // antd only defaults align for a HORIZONTAL space; a vertical one with no
  // align stays stretched, which is what makes vertical Spaces full-width.
  const mergedAlign =
    align === undefined && direction === 'horizontal' ? 'center' : align

  const items = toItems(children)

  if (!items.length) return null

  return (
    <div
      {...rest}
      ref={ref}
      data-slot="space"
      className={cn(
        'ant-space inline-flex',
        `ant-space-${direction}`,
        direction === 'vertical' ? 'flex-col' : 'flex-row',
        wrap && 'flex-wrap',
        mergedAlign && `ant-space-align-${mergedAlign}`,
        mergedAlign && ALIGN_CLASS[mergedAlign],
        className
      )}
      style={{
        columnGap: horizontalSize,
        rowGap: verticalSize,
        ...style,
      }}
    >
      {items.map((child, index) => (
        <React.Fragment
          // eslint-disable-next-line react/no-array-index-key -- items have no
          // identity of their own; the index IS the slot they occupy. A
          // child's own key cannot be used: `React.Children.toArray` restarts
          // its generated key sequence at `.0` for every call, so a flattened
          // fragment would collide with its plain siblings.
          key={`space-item-${index}`}
        >
          <div data-slot="space-item" className="ant-space-item">
            {child}
          </div>
          {split && index < items.length - 1 ? (
            <span data-slot="space-item-split" className="ant-space-item-split">
              {split}
            </span>
          ) : null}
        </React.Fragment>
      ))}
    </div>
  )
})

SpaceBase.displayName = 'Space'

/* ------------------------------------------------------------------ Compact */

export type SpaceCompactProps = {
  direction?: 'horizontal' | 'vertical'
  block?: boolean
  size?: 'small' | 'middle' | 'large'
  children?: React.ReactNode
} & React.HTMLAttributes<HTMLDivElement>

/**
 * `Space.Compact` — the joined-control row (a search input welded to its
 * button). Seven live call sites on this branch, almost all
 * `<Space.Compact style={{ width: '100%' }}>` around an Input plus a Button.
 *
 * antd implements the seam between children with a CONTEXT that each of
 * Button/Input/Select reads in order to add `-compact-item` classes and drop
 * its inner border radii. This seam does the same job with CSS on its direct
 * children instead, which is why it needs no changes in the Button seam and
 * none at all in the un-ported Input and Select — worth knowing before
 * "fixing" it to match antd's protocol.
 *
 * The `-ml-px` is what collapses the doubled border between two adjacent
 * controls, and `focus-within:z-10` keeps the focused control's ring on top of
 * its neighbour instead of half-hidden underneath.
 */
const SpaceCompact = React.forwardRef<HTMLDivElement, SpaceCompactProps>(
  (props, ref) => {
    const {
      direction = 'horizontal',
      block,
      // antd sizes the CHILD controls through its compact context; this seam
      // has no such protocol (see the note above), so the prop is accepted and
      // ignored rather than leaked to the DOM.
      size: _size,
      className,
      style,
      children,
      // antd-only props that must never reach the DOM. `./Space.tsx` types the
      // Compact dispatcher as antd's own full prop shape, so `rootClassName`
      // reaches here too.
      prefixCls: _prefixCls,
      rootClassName: _rootClassName,
      ...rest
    } = props as SpaceCompactProps & {
      prefixCls?: string
      rootClassName?: string
    }

    const vertical = direction === 'vertical'

    return (
      <div
        {...rest}
        ref={ref}
        data-slot="space-compact"
        className={cn(
          'ant-space-compact inline-flex',
          vertical ? 'ant-space-compact-vertical flex-col' : 'flex-row',
          block && 'flex w-full',
          // Collapse the shared border and round only the outer corners.
          '[&>*]:relative focus-within:[&>*]:z-10',
          vertical
            ? '[&>*:not(:first-child)]:-mt-px [&>*:not(:first-child):not(:last-child)]:rounded-none [&>*:first-child:not(:last-child)]:rounded-b-none [&>*:last-child:not(:first-child)]:rounded-t-none'
            : '[&>*:not(:first-child)]:-ml-px [&>*:not(:first-child):not(:last-child)]:rounded-none [&>*:first-child:not(:last-child)]:rounded-r-none [&>*:last-child:not(:first-child)]:rounded-l-none',
          className
        )}
        style={style}
      >
        {children}
      </div>
    )
  }
)

SpaceCompact.displayName = 'Space.Compact'

export const Space = Object.assign(SpaceBase, { Compact: SpaceCompact })
